/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { FormGroup } from '@angular/forms';
import { Filterable } from '@gh/core-data';
import { create } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { FormState } from './form-state';

export type FilterableFormState<F> = FormState<F> & Filterable<F>;

/**
 * This mixin merges {@link Filterable} behavior into provided {@link FormState}.
 */
export function mixinFilterableForm<F>(base: Constructor<FormState<F>>): Constructor<FilterableFormState<F>> {
  return class extends base {
    filterChanges: Subject<F>;

    get currentFilter(): F {
      return this.submitValue;
    }

    setFilter(filter: F): void {
      this.group.patchValue(filter);
      this.emitFilter();
    }

    emitFilter(): void {
      this.filterChanges.next(this.submitValue);
    }

    protected setGroup(group: FormGroup): void {
      super.setGroup(group);

      this.filterChanges = new BehaviorSubject<F>(this.submitValue);
    }
  };
}

/**
 * This function merges {@link Filterable} behavior into provided {@link FormState}.
 */
export function filterableForm<F>(formState: FormState<F>): FilterableFormState<F> {
  const filterChanges$$ = new BehaviorSubject(formState.submitValue);

  function setFilter(filter: F): void {
    delegated.group.patchValue(filter);
    emitFilter();
  }

  function emitFilter(): void {
    filterChanges$$.next(delegated.submitValue);
  }

  const delegated = <FilterableFormState<F>>create(
    formState,
    {
      filterChanges: filterChanges$$.asObservable(),
      setFilter,
      emitFilter,
    });

  Object.defineProperty(delegated, 'currentFilter', {
    get: () => delegated.submitValue,
  });

  return delegated;
}
